import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Dropdown,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  CloudDownloadOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
  ToolOutlined,
  MoreOutlined,
  SearchOutlined,
} from '@ant-design/icons';

import AppCard from '../../../components/cards/AppCard';
import OtpGateModal from '../../../components/admin/OtpGateModal';
import {
  createRowActionConfig,
  deleteRowActionConfig,
  getAllRoles,
  getModules,
  getCustomForms,
  getRowActionConfigs,
  updateRowActionConfig,
} from '../../../services/adminApi';
import '../../../styles/FormGroupsSection.css';

// OTP-gated config actions surfaced as buttons next to "Add Action". Each opens
// the OtpGateModal which mails a code and performs the action on verification.
// These operate on ALL modules'/roles' row-action configs at once (not just the
// currently selected module), same as the Form Groups / Detail Groups pattern.
const CONFIG_GATE_META = {
  reset:   { action: 'reset',   title: 'Reset all row actions',   confirmLabel: 'Reset All',       danger: true,
    description: 'This clears every configured row action for every module in this workspace. Actions have no built-in defaults, so this leaves every module with no actions until reconfigured.' },
  save:    { action: 'save',    title: 'Save current as default', confirmLabel: 'Save Default',    danger: false,
    description: 'Snapshots the current row actions for all modules and roles as the saved default you can restore to later.' },
  restore: { action: 'restore', title: 'Restore saved default',   confirmLabel: 'Restore Default', danger: true,
    description: 'Replaces the current row actions for all modules and roles with the last saved default.' },
};

const { Text } = Typography;

const ICON_OPTIONS = [
  { value: 'delete',      label: 'delete (trash)' },
  { value: 'edit',        label: 'edit (pencil)' },
  { value: 'mail',        label: 'mail (envelope)' },
  { value: 'shareAlt',    label: 'shareAlt (share)' },
  { value: 'swap',        label: 'swap (arrows)' },
  { value: 'thunderbolt', label: 'thunderbolt (lightning)' },
  { value: 'userAdd',     label: 'userAdd (add user)' },
  { value: 'userSwitch',  label: 'userSwitch (switch user)' },
];

const ACTION_TYPE_OPTIONS = [
  { value: 'navigate', label: 'Navigate (open page)' },
  { value: 'modal',    label: 'Modal (open popup)' },
  { value: 'api',      label: 'API (call endpoint)' },
  { value: 'copy',     label: 'Copy (clipboard)' },
  { value: 'document', label: 'Document (open viewer)' },
];

const API_METHOD_OPTIONS = ['GET', 'POST', 'PUT', 'DELETE'];
const BUILT_IN_POPUPS = [
  'quick-submit', 'add-notes', 'mail', 'change-status', 'tag-to-job',
  'add-to-groups', 'self-assign', 'others-assign', 'delete', 'vendor-share',
  'sticky-notes', 'move-to-onboarding', 'approve-rate', 'reject',
];

const EMPTY_ACTION = {
  key: '', label: '', icon: '', actionType: 'navigate',
  routeTemplate: '', apiMethod: 'POST', apiPath: '',
  popupKey: '', popupStateText: '', copyField: '', documentField: '', allowedTabs: [],
  permission: '', permissionKey: '', allowMultiple: false, danger: false, showInList: true,
  showInDetail: false, detailPlacement: 'more',
};

function newKey() {
  return `action_${Date.now()}`;
}

/* ── Action editor ─────────────────────────────────────────────────────────── */
function ActionModal({ open, initial, popupForms, onClose, onSave }) {
  const [form] = Form.useForm();
  const actionType = Form.useWatch('actionType', form) ?? 'navigate';
  const showInDetail = Form.useWatch('showInDetail', form) ?? false;

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        ...EMPTY_ACTION,
        ...initial,
        popupStateText: initial?.popupState
          ? JSON.stringify(initial.popupState, null, 2)
          : '',
      });
    }
  }, [open, initial, form]);

  const handleSave = async () => {
    const values = await form.validateFields();
    let popupState;
    if (values.popupStateText?.trim()) {
      try {
        popupState = JSON.parse(values.popupStateText);
      } catch {
        form.setFields([{ name: 'popupStateText', errors: ['Enter valid JSON'] }]);
        return;
      }
    }
    const action = { ...values };
    delete action.popupStateText;
    onSave({ ...action, ...(popupState ? { popupState } : { popupState: undefined }) });
  };

  const popupOptions = [
    ...BUILT_IN_POPUPS.map((key) => ({ value: key, label: `${key} (built-in)` })),
    ...popupForms.map((popup) => ({
      value: popup.action,
      label: `${popup.label} (${popup.action})`,
    })),
  ].filter((option, index, options) => options.findIndex((item) => item.value === option.value) === index);

  return (
    <Modal
      title={initial?.key ? 'Edit Action' : 'Add Action'}
      width={720}
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      okText="Save Action"
      okButtonProps={{ icon: <SaveOutlined /> }}
      className="fg-editor-modal action-editor-modal"
    >
      <Form form={form} layout="vertical" initialValues={EMPTY_ACTION} className="action-editor-form">
        <div className="action-editor-intro">
          Choose what the action does, then decide where it appears and who can use it.
        </div>
        <div className="action-editor-grid">
        <Form.Item name="key" label="Key" rules={[{ required: true, message: 'Key is required' }]}>
          <Input placeholder="e.g. sourceCandidates" />
        </Form.Item>
        <Form.Item name="label" label="Label" rules={[{ required: true, message: 'Label is required' }]}>
          <Input placeholder="e.g. Source Candidates" />
        </Form.Item>
        <Form.Item name="icon" label="Icon" rules={[{ required: false, message: 'Icon is required' }]}>
          <Select options={ICON_OPTIONS} placeholder="Select icon" allowClear/>
        </Form.Item>
        <Form.Item name="actionType" label="Action Type" rules={[{ required: true }]}>
          <Select
            options={ACTION_TYPE_OPTIONS}
          />
        </Form.Item>
        </div>

        <div className="action-editor-section-title">Behaviour</div>

        {actionType === 'navigate' && (
          <Form.Item name="routeTemplate" label="Route Template" rules={[{ required: true }]}>
            <Input placeholder="e.g. /app/jobs/edit/:_id" />
          </Form.Item>
        )}

        {actionType === 'modal' && (
          <>
            <Form.Item name="popupKey" label="Popup" rules={[{ required: true, message: 'Select a popup' }]}
              tooltip="Admin popup forms and built-in popup integrations are listed together.">
              <Select showSearch optionFilterProp="label" options={popupOptions} placeholder="Select popup form" />
            </Form.Item>
            <Form.Item name="popupStateText" label="Popup Context (JSON, optional)"
              tooltip='Static context passed to the popup, for example {"notesfor":"candidate"}'>
              <Input.TextArea rows={4} placeholder={'{"notesfor":"candidate"}'} />
            </Form.Item>
            <Space.Compact block>
              <Form.Item name="apiMethod" label="Submit Method" style={{ width: 150 }}>
                <Select options={API_METHOD_OPTIONS.map((m) => ({ value: m, label: m }))} />
              </Form.Item>
              <Form.Item name="apiPath" label="Submit API (optional)" style={{ flex: 1 }}
                tooltip="Leave empty to use the built-in handler for the selected popup key.">
                <Input placeholder="e.g. /module-data-update" />
              </Form.Item>
            </Space.Compact>
          </>
        )}

        {actionType === 'api' && (
          <>
            <Form.Item name="apiMethod" label="API Method">
              <Select options={API_METHOD_OPTIONS.map((m) => ({ value: m, label: m }))} />
            </Form.Item>
            <Form.Item name="apiPath" label="API Path">
              <Input placeholder="e.g. /jobs/:_id" />
            </Form.Item>
          </>
        )}

        {actionType === 'copy' && (
          <Form.Item name="copyField" label="Record Field to Copy" rules={[{ required: true }]}>
            <Input placeholder="e.g. email or applicantRefrenceId" />
          </Form.Item>
        )}

        {actionType === 'document' && (
          <Form.Item name="documentField" label="Document Field" rules={[{ required: true }]}>
            <Input placeholder="e.g. resume or attachment" />
          </Form.Item>
        )}

        <div className="action-editor-section-title">Availability and access</div>
        <Form.Item name="allowedTabs" label="Allowed Tabs (optional)"
          tooltip="Leave empty to show on every tab.">
          <Select mode="tags" tokenSeparators={[',']} placeholder="e.g. allJobs" />
        </Form.Item>

        <Form.Item name="permission" label="Role Permission (optional)">
          <Input placeholder="e.g. job.edit" />
        </Form.Item>
        <Form.Item name="permissionKey" label="Permission Key (row-level gate)" tooltip="Maps to record.actionPermission.<key>. Leave blank to always show.">
          <Input placeholder="e.g. edit, sourceCandidates, sharePermission" />
        </Form.Item>

        <div className="action-toggle-grid">
          <Form.Item name="allowMultiple" label="Multiple Records" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="danger" label="Danger" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="showInList" label="Show in List" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="showInDetail" label="Show in Detail" valuePropName="checked">
            <Switch />
          </Form.Item>
        </div>
        {showInDetail && (
          <Form.Item name="detailPlacement" label="Detail Placement">
            <Select options={[
              { value: 'primary', label: 'Primary button' },
              { value: 'more', label: 'More actions menu' },
            ]} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}

/* ── Main Section ──────────────────────────────────────────────────────────── */
export default function RowActionsSection() {
  const [modules, setModules] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedModule, setSelectedModule] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState(0);

  const [configs, setConfigs] = useState([]);
  const [activeConfig, setActiveConfig] = useState(null);
  const [actions, setActions] = useState([]);
  const [dirty, setDirty] = useState(false);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [popupForms, setPopupForms] = useState([]);
  const [gate, setGate] = useState(null); // { action, title, confirmLabel, danger, description }
  const [query, setQuery] = useState('');

  /* Load modules + roles once */
  useEffect(() => {
    Promise.all([getModules(), getAllRoles()]).then(([mods, { roles: r }]) => {
      const modList = Array.isArray(mods) ? mods.map((m) => m.module ?? m.key ?? m.value ?? m) : [];
      const unique = [...new Set(modList.filter(Boolean))];
      setModules(unique);
      setSelectedModule((current) => current || unique[0] || '');
      setRoles(r ?? []);
    }).catch(() => {
      setModules([]);
    });
  }, []);

  /* Load configs when module changes */
  const loadConfigs = useCallback(async () => {
    if (!selectedModule) return;
    setLoading(true);
    setError(null);
    setConfigs([]);
    try {
      const data = await getRowActionConfigs(selectedModule);
      setConfigs(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [selectedModule]);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  useEffect(() => {
    getCustomForms(selectedModule, 'popup')
      .then(setPopupForms)
      .catch(() => setPopupForms([]));
  }, [selectedModule]);

  /* When role selection changes, find matching config from already-loaded configs */
  useEffect(() => {
    const match = configs.find((c) => c.roleId === selectedRoleId);
    setActiveConfig(match ?? null);
    setActions(match ? (match.rowActions ?? []) : []);
    setDirty(false);
  }, [selectedRoleId, configs]);

  /* Save */
  const handleSave = async () => {
    setSaving(true);
    try {
      if (activeConfig) {
        await updateRowActionConfig(activeConfig.id ?? activeConfig._id, actions);
        setConfigs((prev) => prev.map((config) => (
          (config.id ?? config._id) === (activeConfig.id ?? activeConfig._id)
            ? { ...config, rowActions: actions }
            : config
        )));
        message.success('Config saved');
      } else {
        const created = await createRowActionConfig({ module: selectedModule, roleId: selectedRoleId, rowActions: actions });
        const newDoc = created?.data ?? created;
        setActiveConfig(newDoc);
        setConfigs((prev) => [...prev, newDoc]);
        message.success('Config created');
      }
      setDirty(false);
    } catch (e) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  /* Delete config */
  const handleDeleteConfig = async () => {
    if (!activeConfig) return;
    try {
      await deleteRowActionConfig(activeConfig.id ?? activeConfig._id);
      message.success('Config deleted');
      await loadConfigs();
    } catch (e) {
      message.error(e.message);
    }
  };

  /* Action row operations */
  const openAddDrawer = () => {
    setEditingAction(null);
    setDrawerOpen(true);
  };

  const openEditDrawer = (action) => {
    setEditingAction(action);
    setDrawerOpen(true);
  };

  const handleSaveAction = (values) => {
    if (editingAction) {
      setActions((prev) => prev.map((a) => (a.key === editingAction.key ? { ...a, ...values } : a)));
    } else {
      setActions((prev) => [...prev, { ...EMPTY_ACTION, ...values, _localKey: newKey() }]);
    }
    setDirty(true);
    setDrawerOpen(false);
  };

  const handleDeleteAction = (actionKey) => {
    setActions((prev) => prev.filter((a) => a.key !== actionKey));
    setDirty(true);
  };

  const moveAction = (index, dir) => {
    setActions((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setDirty(true);
  };

  /* OTP-gated Reset All / Save Default / Restore Default */
  function openGate(action) { setGate(CONFIG_GATE_META[action]); }
  function handleGateVerified() {
    setGate(null);
    loadConfigs();
  }

  const roleOptions = useMemo(() => [
    { value: 0, label: 'All Roles (default)' },
    ...roles.map((r) => ({ value: r.roleId, label: r.roleName })),
  ], [roles]);

  const visibleActions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return actions;
    return actions.filter((action) => [
      action.label, action.key, action.actionType, action.popupKey,
      action.routeTemplate, action.apiPath, action.permissionKey,
    ].some((value) => String(value ?? '').toLowerCase().includes(needle)));
  }, [actions, query]);

  const columns = [
    { title: 'Order', key: 'order', width: 76,
      render: (_, action) => (
        <Space>
          <Button size="small" type="text" disabled={action._actionIndex === 0} onClick={() => moveAction(action._actionIndex, -1)}>↑</Button>
          <Button size="small" type="text" disabled={action._actionIndex === actions.length - 1} onClick={() => moveAction(action._actionIndex, 1)}>↓</Button>
        </Space>
      ),
    },
    { title: 'Action', key: 'identity', width: 210, render: (_, action) => (
      <div className="admin-config-primary-cell">
        <Text strong>{action.label || action.key}</Text>
        <Text type="secondary" code>{action.key}</Text>
      </div>
    ) },
    { title: 'Behaviour', key: 'destination', render: (_, action) => (
      <div className="admin-config-primary-cell">
        <Tag color="blue">{action.actionType}</Tag>
        <Text type="secondary" ellipsis={{ tooltip: true }}>
          {action.popupKey || action.routeTemplate || action.apiPath || action.copyField || action.documentField || 'No destination'}
        </Text>
      </div>
    ),
    },
    { title: 'Visible in', key: 'visibility', width: 210, render: (_, action) => (
      <Space size={[4, 4]} wrap>
        {action.showInList && <Tag color="green">List</Tag>}
        {(action.showInDetail ?? action.showInList) && <Tag color="purple">Detail · {action.detailPlacement || 'more'}</Tag>}
        {action.allowMultiple && <Tag>Multi-select</Tag>}
        {action.danger && <Tag color="red">Destructive</Tag>}
        {!action.showInList && !(action.showInDetail ?? action.showInList) && <Text type="secondary">Nowhere</Text>}
      </Space>
    ) },
    { title: 'Access', key: 'access', width: 170, render: (_, action) => (
      <div className="admin-config-primary-cell">
        <Text type="secondary">{action.permission || 'All permitted roles'}</Text>
        {action.permissionKey && <Text type="secondary" code>{action.permissionKey}</Text>}
      </div>
    ),
    },
    { title: '', key: 'actions', width: 80, fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEditDrawer(record)} />
          </Tooltip>
          <Popconfirm title="Remove this action?" onConfirm={() => handleDeleteAction(record.key)} okText="Remove" okType="danger">
            <Tooltip title="Remove">
              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="fg-section admin-config-section">

      {/* Header card */}
      <AppCard
        icon={<ToolOutlined />}
        title="Actions"
        extra={
          <Dropdown trigger={['click']} menu={{ items: [
            { key: 'save', label: 'Save current as default', icon: <SaveOutlined />, onClick: () => openGate('save') },
            { key: 'restore', label: 'Restore saved default', icon: <CloudDownloadOutlined />, onClick: () => openGate('restore') },
            { type: 'divider' },
            { key: 'reset', label: 'Reset all actions', icon: <ReloadOutlined />, danger: true, onClick: () => openGate('reset') },
          ] }}>
            <Button size="small" icon={<MoreOutlined />}>Workspace defaults</Button>
          </Dropdown>
        }
      >
        <div className="admin-accent-bar" />
        <Text type="secondary">
          Build one action once, then control its List and Detail placement, permissions, tabs, and multi-select behaviour.
        </Text>
      </AppCard>

      {/* Selector + controls */}
      <AppCard styles={{ body: { padding: '14px 18px' } }}>
        <div className="admin-config-context">
          <div className="admin-config-context-copy">
            <Text strong>Configuration context</Text>
            <Text type="secondary">Choose a module and role. “All Roles” is the fallback configuration.</Text>
          </div>
          <div className="admin-config-context-controls">
          <label className="admin-config-control">
            <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>Module</Text>
            <Select
              showSearch optionFilterProp="label" style={{ width: 200 }}
              value={selectedModule}
              onChange={(v) => setSelectedModule(v)}
              options={modules.map((m) => ({ value: m, label: m }))}
            />
          </label>
          <label className="admin-config-control">
            <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>Role</Text>
            <Select
              style={{ width: 220 }}
              value={selectedRoleId}
              onChange={(v) => setSelectedRoleId(v)}
              options={roleOptions}
            />
          </label>
          <div className="admin-config-reload">
            <Button icon={<ReloadOutlined />} onClick={loadConfigs} loading={loading}>
              Reload
            </Button>
          </div></div>
        </div>
      </AppCard>

      {/* Error */}
      {error && <Alert type="error" message={error} showIcon />}

      {/* Actions table */}
      <AppCard
        icon={<ToolOutlined />}
        title={
          <Space wrap>
            <span>{actions.length} configured action{actions.length === 1 ? '' : 's'}</span>
            {activeConfig
              ? <Tag color="green">Config loaded (roleId: {selectedRoleId})</Tag>
              : <Tag color="orange">No config — will create on Save</Tag>
            }
          </Space>
        }
        extra={
          <Space wrap>
            {activeConfig && (
              <Popconfirm title="Delete this entire config?" onConfirm={handleDeleteConfig} okText="Delete" okType="danger">
                <Button danger size="small" icon={<DeleteOutlined />}>Delete Config</Button>
              </Popconfirm>
            )}
            <Button icon={<PlusOutlined />} onClick={openAddDrawer}>New action</Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              disabled={!dirty}
              onClick={handleSave}
            >
              Save Config
            </Button>
          </Space>
        }
      >
        <div className="admin-config-table-tools">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search actions"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {dirty && <Text type="warning">Unsaved changes</Text>}
        </div>
        <Spin spinning={loading}>
          <Table
            dataSource={visibleActions.map((a, i) => ({
              ...a,
              _actionIndex: actions.indexOf(a),
              _rowKey: a.key || a._localKey || i,
            }))}
            rowKey="_rowKey"
            columns={columns}
            pagination={false}
            size="small"
            className="fg-table admin-config-table"
            scroll={{ x: 820 }}
            locale={{ emptyText: 'No actions configured. Click "Add Action" to start.' }}
          />
        </Spin>
      </AppCard>

      <ActionModal
        open={drawerOpen}
        initial={editingAction}
        popupForms={popupForms}
        onClose={() => setDrawerOpen(false)}
        onSave={handleSaveAction}
      />

      <OtpGateModal
        open={!!gate}
        scope="rowActions"
        action={gate?.action}
        title={gate?.title}
        description={gate?.description}
        confirmLabel={gate?.confirmLabel}
        danger={gate?.danger}
        onCancel={() => setGate(null)}
        onVerified={handleGateVerified}
      />
    </div>
  );
}
